import React from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import Image from "next/image";
import heroImage from "@/public//img/contact/hero.jpg";
import ContactForm from "@/components/contact/contact-form";
import Link from "next/link";
import { m } from "framer-motion";

const translationNamespaces = ["common", "nav", "contact"];

export const getStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, translationNamespaces)),
    },
  };
};

const Contact = () => {
  return (
    <div className="flex flex-col">
      <NextSeo
        title="Contact Us"
        description="Contact us for any inquiries or to get in touch with us. We are always here to help you."
        canonical="https://alhayatfoodstuff.com/contact"
        openGraph={{
          title: "Contact Us",
          description:
            "Contact us for any inquiries or to get in touch with us. We are always here to help you.",
        }}
      />
      <section className="relative flex h-60 w-full flex-col items-center justify-center bg-black/50">
        <Image
          src={heroImage}
          alt=""
          priority
          placeholder="blur"
          fill
          className="absolute inset-0 -z-10 h-full w-full object-cover object-center md:object-[50%_35%] xl:object-[50%_30%]"
          sizes="(max-width: 600px) 600px, (max-width: 900px) 900px, (max-width: 1200px) 1200px, (max-width: 1500px) 1500px, 2000px"
        />
        <h1 className="z-10 pt-0 text-4xl font-bold text-white sm:text-5xl md:text-6xl">
          Contact Us
        </h1>
      </section>
      <section className="sm:bg-auto-black/5">
        <div className="container flex flex-col items-start justify-center gap-6 p-8 py-16 md:grid md:grid-cols-2 md:py-24 lg:grid-cols-4">
          <m.div
            className="flex flex-col items-start justify-center gap-4 md:order-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          >
            <div className="self-stretch text-2xl font-bold leading-loose text-primary-700">
              Contact Info
            </div>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                />
              </svg>
              <div className="text-md font-medium text-auto-black/70">
                +48 514 499 212
              </div>
            </div>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                />
              </svg>

              <div className="text-md font-medium text-auto-black/70">
                info.pl@alhayatfoodstuff.com
              </div>
            </div>
          </m.div>
          <m.div
            className="sm:card relative w-full sm:p-10 md:order-3 md:row-span-4 lg:col-span-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.7 }}
          >
            <ContactForm />
          </m.div>
          <m.div
            className="flex flex-col items-start justify-start gap-6 md:order-3 lg:order-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          >
            <div className="self-stretch text-2xl font-bold leading-loose text-primary-700">
              Our Offices
            </div>
            <div className="flex flex-col items-start justify-start gap-4 self-stretch">
              <div className="self-stretch text-lg font-semibold leading-7 text-auto-black/70">
                Al Hayat Food Stuff sp. z o.o.
              </div>
              <div className="flex items-start justify-start gap-2 self-stretch">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                  />
                </svg>

                <div className="shrink grow basis-0 text-sm font-medium leading-tight text-auto-black/60">
                  Kamionna 22A
                  <br />
                  06-120 Winnica
                  <br />
                  Poland
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start justify-start gap-4 self-stretch">
              <div className="self-stretch text-lg font-semibold leading-7 text-auto-black/70">
                AlHayat Foodstuff Co. W.L.L.
              </div>
              <div className="flex items-start justify-start gap-2 self-stretch">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                  />
                </svg>
                <div className="shrink grow basis-0 text-sm font-medium leading-tight text-auto-black/60">
                  455 Salwa Road
                  <br />
                  Doha
                  <br />
                  Qatar
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start justify-start gap-4 self-stretch">
              <div className="self-stretch text-lg font-semibold leading-7 text-auto-black/70">
                Al Hayat Foodstuff W.L.L.
              </div>
              <div className="flex items-start justify-start gap-2 self-stretch">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                  />
                </svg>
                <div className="shrink grow basis-0 text-sm font-medium leading-tight text-auto-black/60">
                  Building 63, Zone 8, Lotus District
                  <br />
                  5th Settlement, New Cairo
                  <br />
                  Egypt
                </div>
              </div>
            </div>
          </m.div>
          <m.div
            className="h-px w-full bg-auto-black/30 md:order-3 lg:order-4 lg:col-span-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          ></m.div>
          <m.div
            className="flex w-1/2 flex-col gap-2 md:order-5 md:w-64 lg:order-2 lg:w-96"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          >
            <div className="self-stretch text-2xl font-bold leading-loose text-primary-700">
              Social Media
            </div>
            <div className="flex gap-8">
              <Link
                href="https://www.facebook.com/AlHayatFoodstuff"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-auto-black p-2 opacity-60 transition-opacity duration-100 hover:opacity-80"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                >
                  <path
                    d="M17 2H14C12.6739 2 11.4021 2.52678 10.4645 3.46447C9.52678 4.40215 9 5.67392 9 7V10H6V14H9V22H13V14H16L17 10H13V7C13 6.73478 13.1054 6.48043 13.2929 6.29289C13.4804 6.10536 13.7348 6 14 6H17V2Z"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="fill-none stroke-auto-black"
                  />
                </svg>
              </Link>
              <Link
                href="https://www.twitter.com/alhayatfoodstuff"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-auto-black p-2  opacity-60 transition-opacity duration-100 hover:opacity-80"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-6 w-6 p-0.5"
                >
                  <path
                    d="M13.941 10.392L21.5355 1.5H19.7355L13.143 9.2205L7.87499 1.5H1.79999L9.76499 13.176L1.79999 22.5H3.59999L10.563 14.346L16.1265 22.5H22.2015L13.941 10.392ZM11.4765 13.278L10.6695 12.1155L4.24799 2.865H7.01249L12.1935 10.3305L13.0005 11.493L19.737 21.198H16.9725L11.4765 13.278Z"
                    className="fill-auto-black"
                  />
                </svg>
              </Link>
              <Link
                href="https://www.linkedin.com/company/alhayatfoodstuff"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-auto-black p-2  opacity-60 transition-opacity duration-100 hover:opacity-80"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5 1.25C4.27065 1.25 3.57118 1.53973 3.05546 2.05546C2.53973 2.57118 2.25 3.27065 2.25 4C2.25 4.72935 2.53973 5.42882 3.05546 5.94454C3.57118 6.46027 4.27065 6.75 5 6.75C5.72935 6.75 6.42882 6.46027 6.94454 5.94454C7.46027 5.42882 7.75 4.72935 7.75 4C7.75 3.27065 7.46027 2.57118 6.94454 2.05546C6.42882 1.53973 5.72935 1.25 5 1.25ZM3.75 4C3.75 3.66848 3.8817 3.35054 4.11612 3.11612C4.35054 2.8817 4.66848 2.75 5 2.75C5.33152 2.75 5.64946 2.8817 5.88388 3.11612C6.1183 3.35054 6.25 3.66848 6.25 4C6.25 4.33152 6.1183 4.64946 5.88388 4.88388C5.64946 5.1183 5.33152 5.25 5 5.25C4.66848 5.25 4.35054 5.1183 4.11612 4.88388C3.8817 4.64946 3.75 4.33152 3.75 4ZM2.25 8C2.25 7.80109 2.32902 7.61032 2.46967 7.46967C2.61032 7.32902 2.80109 7.25 3 7.25H7C7.19891 7.25 7.38968 7.32902 7.53033 7.46967C7.67098 7.61032 7.75 7.80109 7.75 8V21C7.75 21.1989 7.67098 21.3897 7.53033 21.5303C7.38968 21.671 7.19891 21.75 7 21.75H3C2.80109 21.75 2.61032 21.671 2.46967 21.5303C2.32902 21.3897 2.25 21.1989 2.25 21V8ZM3.75 8.75V20.25H6.25V8.75H3.75ZM9.25 8C9.25 7.80109 9.32902 7.61032 9.46967 7.46967C9.61032 7.32902 9.80109 7.25 10 7.25H14C14.1989 7.25 14.3897 7.32902 14.5303 7.46967C14.671 7.61032 14.75 7.80109 14.75 8V8.434L15.185 8.247C15.935 7.92648 16.7307 7.7257 17.543 7.652C20.318 7.4 22.75 9.58 22.75 12.38V21C22.75 21.1989 22.671 21.3897 22.5303 21.5303C22.3897 21.671 22.1989 21.75 22 21.75H18C17.8011 21.75 17.6103 21.671 17.4697 21.5303C17.329 21.3897 17.25 21.1989 17.25 21V14C17.25 13.6685 17.1183 13.3505 16.8839 13.1161C16.6495 12.8817 16.3315 12.75 16 12.75C15.6685 12.75 15.3505 12.8817 15.1161 13.1161C14.8817 13.3505 14.75 13.6685 14.75 14V21C14.75 21.1989 14.671 21.3897 14.5303 21.5303C14.3897 21.671 14.1989 21.75 14 21.75H10C9.80109 21.75 9.61032 21.671 9.46967 21.5303C9.32902 21.3897 9.25 21.1989 9.25 21V8ZM10.75 8.75V20.25H13.25V14C13.25 13.2707 13.5397 12.5712 14.0555 12.0555C14.5712 11.5397 15.2707 11.25 16 11.25C16.7293 11.25 17.4288 11.5397 17.9445 12.0555C18.4603 12.5712 18.75 13.2707 18.75 14V20.25H21.25V12.38C21.25 10.476 19.589 8.972 17.68 9.146C17.0242 9.20567 16.3817 9.36763 15.776 9.626L14.296 10.261C14.1818 10.31 14.0573 10.3299 13.9336 10.3189C13.8098 10.3079 13.6907 10.2663 13.587 10.1979C13.4833 10.1295 13.3982 10.0364 13.3394 9.927C13.2806 9.81757 13.2499 9.69524 13.25 9.571V8.75H10.75Z"
                    className="fill-auto-black"
                  />
                </svg>
              </Link>
            </div>
          </m.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
